/** 
 * @description Methods for controlling product page functionality.
 */

import React from 'react';
import ReactDOM from 'react-dom';
import throttle from '../util/throttle';
import smoothScrollIntoView from '../util/smoothScrollIntoView';
import isAnimationSupported from '../util/isAnimationSupported';
import addOrReplaceQueryStringParameter from '../util/addOrReplaceQueryStringParameter';
import getQueryStringParameter from '../util/getQueryStringParameter';
import inPunchoutSession from '../util/inPuchoutSession';
import isUserLoggedIn from '../util/isUserLoggedIn';
import trackEvent from '../util/trackEvent';
import QuantityInput from '../react/components/QuantityInput.jsx';
import { COLONY as DEFAULT_BREAKPOINT } from '../shell/breakpoints';
import * as Modal from '../components/modal';
import FileDownload from '../components/fileDownload.jsx';
import supportedLanguages from '../util/supportedLanguages';

const OPTION_CONTROL_CLASS = "product-selection__option-control",
    OPTION_DROPDOWN_CLASS = "product-selection__option-dropdown",
    CATALOG_NUMBER_CLASS = "product-option__catalog-number",
    CATALOG_LABEL_CLASS = "product-option__catalog-label",
    CATALOG_LABEL_HIDDEN_CLASS = "product-option__catalog-label--hidden",
    PRICING_CONTAINER_CLASS = "product-purchase__pricing-container",
    PRICING_CONTAINER_HIDDEN_CLASS = "product-purchase__pricing-container--hidden",
    LIST_PRICE_CLASS = "product-purchase__list-price",
    YOUR_PRICE_CLASS = "product-purchase__your-price",
    YOUR_PRICE_CONTAINER_CLASS = "product-purchase__your-price-container",
    RESTRICTION_MESSAGE_CLASS = "product-purchase__restriction-message",
    RESTRICTION_MESSAGE_HIDDEN_CLASS = "product-purchase__restriction-message--hidden",
    CART_CONTROLS_CLASS = "product-purchase__cart-controls",
    CART_CONTROLS_HIDDEN_CLASS = "product-purchase__cart-controls--hidden",
    QTY_CONTAINER_CLASS = "product-quantity__input-container",
    QTY_CONTAINER_HIDDEN_CLASS = "product-quantity__input-container--hidden",
    ADD_TO_CART_BUTTON_CLASS = "product-purchase__add-to-cart",
    ADD_TO_CART_BUTTON_HIDDEN_CLASS = "product-purchase__add-to-cart--hidden",
    PRODUCT_DISCONTINUED_CLASS = "product-purchase__product-discontinued",
    PRODUCT_DISCONTINUED_HIDDEN_CLASS = "product-purchase__product-discontinued--hidden",
    PRODUCT_EARLYACCESS_CLASS = "product-purchase__product-earlyaccess",
    PRODUCT_EARLYACCESS_HIDDEN_CLASS = "product-purchase__product-earlyaccess--hidden",
    PRODUCT_FASTTRACK_CLASS = "product-purchase__product-fasttrack",
    PRODUCT_FASTTRACK_HIDDEN_CLASS = "product-purchase__product-fasttrack--hidden",
    PRODUCT_PAGE_SECTION_CLASS = "product-page__section",
    PRODUCT_PURCHASE_BLOCK_CLASS = "product-purchase-block",
    STICKY_SELECTION_CLASS = "product-sticky-selection",
    STICKY_SELECTION_VISIBLE_CLASS = "product-sticky-selection--visible",
    STICKY_SELECTION_PRICE_CONTAINER_CLASS = "product-sticky-selection__price-container",
    STICKY_SELECTION_PRICE_CONTAINER_HIDDEN_CLASS = "product-sticky-selection__price-container--hidden",
    STICKY_YOUR_PRICE_CONTAINER_CLASS = "product-sticky-selection__sticky-your-price-container",
    STICKY_SELECTION_PRICE_CLASS = "product-sticky-selection__price",
    STICKY_SELECTION_YOUR_PRICE_CLASS = "product-sticky-selection__your-price",
    STICKY_SELECTION_CONFIG_CLASS = "product-sticky-selection__current-config",
    STICKY_SELECTION_CHANGE_CONFIG_CLASS = "product-sticky-selection__change-config",
    OPTION_CONTAINER_CLASS = "product-purchase__option-container",
    OPTION_CONTAINER_ATTENTION_CLASS = "product-purchase__option-container--attention",
    SPECIFICATIONS_SECTION_CLASS = "product-page__section--specifications",
    PRODUCT_OPTION_MODAL_DIALOG_CLASS = "modal-dialog--product-options",
    PRODUCT_PURCHASE_SPACEHOLDER_CLASS = "product-purchase-spaceholder",
    DIMMER_CLASS = "product-purchase-attention-dimmer",
    SCROLL_CHECK_INTERVAL = 100,
    SMOOTH_SCROLL_DUR = 500;

let catalogNumbers = null,
    currentlySelectedCatalogNumber = null,
    commerceRedirectPath = "/Commerce/CommerceRedirect.aspx",
    currentQuantity = 1;

// Private functions/listeners

function cleanCatalogNumbers(catNums) {
    for (let i = 0; i < catNums.length; i++) {
        if (catNums[i].listPrice === catNums[i].yourPrice) catNums[i].listPrice = null;
        if (catNums[i].listPrice === "") catNums[i].listPrice = null;
        if (catNums[i].yourPrice === "") catNums[i].yourPrice = null;
    }
    return catNums;
}

function optionControlChanged(event) {
    const allSelectedOptions = getAllSelectedOptions();
    currentlySelectedCatalogNumber = getSelectedCatalogNumber(catalogNumbers, allSelectedOptions);

    updateValidSecondaryOptions();
    updateDisplayedCatalogNumber(currentlySelectedCatalogNumber);
    updateAddToCartButtonLink(currentlySelectedCatalogNumber);

    if (event) {
        const eventSource = event.target.closest(".modal-dialog") ? "Modal" : "Inline";
        trackEvent("Components", "Product purchase - selected an option", eventSource);
    }
}

function handleValueChange(value) {
    currentQuantity = value;
    updateAddToCartButtonLink(currentlySelectedCatalogNumber);
}

function renderQuantityInput() {
    const qtyPlaceholder = document.querySelector(`.quantity-input`);
    if (qtyPlaceholder) {
        ReactDOM.hydrate(React.createElement(QuantityInput, { min: 1, max: 999, onValueChange: handleValueChange, isDisabled: false }), qtyPlaceholder.parentElement);
    }
}

function trackAddToCartButtonClick(event) {
    const eventSource = event.target.closest(".product-sticky-selection") ? "Sticky header" : "Inline";
    trackEvent("Components", "Product purchase - clicked Add to Cart", eventSource);
}

// Product page contains an additional sticky header with purchasing controls. Show/hide it depending on how far the user has scrolled.
function updateStickySelectionOnScroll(event) {
    const stickySelection = document.getElementsByClassName(STICKY_SELECTION_CLASS)[0],
        purchasingOptions = document.getElementsByClassName(OPTION_CONTAINER_CLASS)[0],
        purchasingBlock = document.getElementsByClassName(PRODUCT_PURCHASE_BLOCK_CLASS)[0],
        addToCartButton = document.getElementsByClassName(ADD_TO_CART_BUTTON_CLASS)[0],
        changeConfigButton = stickySelection != null ? stickySelection.getElementsByClassName(STICKY_SELECTION_CHANGE_CONFIG_CLASS)[0] : null,
        cartControls = document.getElementsByClassName(CART_CONTROLS_CLASS)[0],
        boundingRect = purchasingBlock.getBoundingClientRect();
    if (stickySelection && purchasingBlock) {
        if (boundingRect.bottom > 0) {
            // Purchasing options are visible on screen. Hide the sticky header.
            if (stickySelection.classList.contains(STICKY_SELECTION_VISIBLE_CLASS)) {
                stickySelection.classList.remove(STICKY_SELECTION_VISIBLE_CLASS);
                if (Modal.isVisible()) Modal.hide();
                if (addToCartButton && cartControls) {
                    cartControls.appendChild(addToCartButton.parentElement.removeChild(addToCartButton));
                }
            }
        }
        else {
            // Purchasing options are above the top of the screen. Show the sticky header.
            if (!stickySelection.classList.contains(STICKY_SELECTION_VISIBLE_CLASS)) {
                stickySelection.classList.add(STICKY_SELECTION_VISIBLE_CLASS);
                if (purchasingOptions) purchasingOptions.classList.remove(OPTION_CONTAINER_ATTENTION_CLASS);
                const pageDim = document.getElementsByClassName(DIMMER_CLASS)[0];
                if (pageDim) pageDim.parentElement.removeChild(pageDim);
                if (addToCartButton && changeConfigButton) {
                    changeConfigButton.parentElement.appendChild(addToCartButton.parentElement.removeChild(addToCartButton));
                }
            }
        }
    }
}

// If we're on desktop, show the purchasing controls in a modal. On mobile there's not enough screen real estate, so just scroll back up to the controls at the top.
function stickyChangeConfigButtonClicked(event) {
    if (event.target && event.target.classList.contains(STICKY_SELECTION_CHANGE_CONFIG_CLASS)) {
        event.preventDefault();
        const purchaseBlock = document.getElementsByClassName(PRODUCT_PURCHASE_BLOCK_CLASS)[0];
        if (purchaseBlock) {
            if (purchaseBlock.getBoundingClientRect().bottom > 0 || !DEFAULT_BREAKPOINT.query.matches) {
                let scrollTargetEl = purchaseBlock,
                    offset = 0;
                if (DEFAULT_BREAKPOINT.query.matches) {
                    scrollTargetEl = document.documentElement;
                }
                else offset = -.5 * scrollTargetEl.getBoundingClientRect().height;
                smoothScrollIntoView(scrollTargetEl, SMOOTH_SCROLL_DUR, {
                    offset: offset, callback: () => {
                        const purchaseOptions = document.getElementsByClassName(OPTION_CONTAINER_CLASS)[0];
                        if (purchaseOptions) {
                            purchaseOptions.classList.add(OPTION_CONTAINER_ATTENTION_CLASS)

                            const pageDim = document.createElement("div");
                            pageDim.className = DIMMER_CLASS;
                            if (isAnimationSupported()) pageDim.addEventListener("animationend", cleanupAttentionDimmer);
                            else setTimeout(cleanupAttentionDimmer, 1000);
                            document.body.appendChild(pageDim);
                        }

                    }
                });
            }
            else {
                showProductOptionsAsFlyout();
            }
        }
    }
}

function cleanupAttentionDimmer() {
    const dimmer = document.getElementsByClassName(DIMMER_CLASS)[0],
        purchaseOptions = document.getElementsByClassName(OPTION_CONTAINER_ATTENTION_CLASS)[0];
    if (dimmer) dimmer.parentElement.removeChild(dimmer);
    if (purchaseOptions) purchaseOptions.classList.remove(OPTION_CONTAINER_ATTENTION_CLASS);
}

// Removes the purchasing controls from their normal position, inserts a placeholder <div> that prevents the page from reflowing, and inserts the controls into the modal.
function showProductOptionsAsFlyout() {
    const productOptions = document.getElementsByClassName(OPTION_CONTAINER_CLASS)[0],
        optionsFlyout = document.getElementsByClassName(PRODUCT_OPTION_MODAL_DIALOG_CLASS)[0],
        anchorEl = document.getElementsByClassName(STICKY_SELECTION_CHANGE_CONFIG_CLASS)[0];

    if (productOptions && optionsFlyout) {
        // Create the spaceholder
        const computedStyles = getComputedStyle(productOptions),
            boundingRect = productOptions.getBoundingClientRect(),
            spaceholder = document.createElement("div");
        spaceholder.className = PRODUCT_PURCHASE_SPACEHOLDER_CLASS;
        spaceholder.style.width = computedStyles.width;
        spaceholder.style.height = computedStyles.height;
        spaceholder.style.marginBottom = computedStyles.marginBottom;
        productOptions.parentNode.insertBefore(spaceholder, productOptions);

        // Move the product options into the flyout
        optionsFlyout.appendChild(productOptions.parentNode.removeChild(productOptions));
        Modal.show(optionsFlyout, anchorEl, {
            afterHide: () => cleanUpProductOptionFlyout(),
            hideOnResize: true,
            width: boundingRect.width
        });

    }
}

function cleanUpProductOptionFlyout() {
    const productOptions = document.getElementsByClassName(OPTION_CONTAINER_CLASS)[0],
        spaceholder = document.getElementsByClassName(PRODUCT_PURCHASE_SPACEHOLDER_CLASS)[0];

    spaceholder.parentNode.insertBefore(productOptions.parentNode.removeChild(productOptions), spaceholder);
    spaceholder.parentNode.removeChild(spaceholder);
}



// API

/**
 * @typedef CatalogNumberDetails
 * @description An object containing details a catalog number and associated options.
 * @type {object}
 * @property {Array<String>} selection An array that lists the combination of options that must be selected in order to match the associated catalog number.
 * @property {String} catNum The catalog number.
 */

/**
 * @function getSelectedCatalogNumber
 * @description Gets the currently selected catalog number.
 * @param {Array<CatalogNumberDetails>} allCatalogNumbers An array of catalog number objects to search
 * @param {Array<HTMLOptionElement>} allSelectedOptions An array of <option> elements
 * @returns {CatalogNumberDetails} An object representing the selected catalog number
 */
export function getSelectedCatalogNumber(allCatalogNumbers, allSelectedOptions) {
    // Sort the list of selected options
    const sortedSelectedOptions = allSelectedOptions.slice(0).sort();

    // Search through the available catalog numbers to find a match
    let matchingCatalogNumber = null;
    for (let i = 0; i < allCatalogNumbers.length; i++) {
        const currentCatNumObj = allCatalogNumbers[i],
            currentCatNumOptions = currentCatNumObj.selection.slice(0).sort();

        if (sortedSelectedOptions.length == currentCatNumOptions.length && sortedSelectedOptions.every((element, index) => element === currentCatNumOptions[index])) {
            matchingCatalogNumber = currentCatNumObj;
            break;
        }
    }
    return matchingCatalogNumber;
}

/**
 * @function selectCatalogNumber
 * @description Given a catalog number object, updates the page state to select it.
 * @param {CatalogNumberDetails} catNum The catalog number object
 */
export function selectCatalogNumber(catNum) {
    const newCatNumObj = catalogNumbers.find((currentValue, index, array) => currentValue.catNum === catNum);
    if (newCatNumObj) {
        // 1. Uncheck all options
        const allOptions = document.querySelectorAll(`.${OPTION_CONTROL_CLASS}`);
        for (let i = 0; i < allOptions.length; i++) {
            if (allOptions[i].tagName.toLowerCase() === "option") allOptions[i].selected = false;
            else allOptions[i].checked = false;
        }

        // 2. Select all the associated options
        for (let k = 0; k < newCatNumObj.selection.length; k++) {
            const optionToSelect = document.querySelector(`.${OPTION_CONTROL_CLASS}[value="${newCatNumObj.selection[k]}"]`);
            if (optionToSelect.tagName.toLowerCase() === "option") optionToSelect.selected = true;
            else optionToSelect.checked = true;
        }

        // 3. Trigger validation
        optionControlChanged();
    }
}

/**
 * @function getAllSelectedOptions
 * @description Gets a list of all currently selected options.
 * @returns {Array<HTMLOptionElement>} An array containing all options that are currently selected
 */
export function getAllSelectedOptions() {
    const optionsNodeList = document.querySelectorAll(".product-selection__option-control:checked"),
        optionsArray = Array.prototype.slice.call(optionsNodeList),
        optionsStringArray = optionsArray.map((currentValue, index, array) => currentValue.value);
    return optionsStringArray;
}

/**
 * @function updateDisplayedCatalogNumber
 * @description Updates the page display to reflect the given catalog number.
 * @param {CatalogNumberDetails} matchingCatalogNumber The catalog number object
 */
export function updateDisplayedCatalogNumber(matchingCatalogNumber) {
    const pricingContainerEl = document.getElementsByClassName(PRICING_CONTAINER_CLASS)[0],
        listPriceEl = document.getElementsByClassName(LIST_PRICE_CLASS)[0],
        yourPriceEl = document.getElementsByClassName(YOUR_PRICE_CLASS)[0],
        yourPriceContainerEl = document.getElementsByClassName(YOUR_PRICE_CONTAINER_CLASS)[0],
        stickyPriceContainerEl = document.getElementsByClassName(STICKY_SELECTION_PRICE_CONTAINER_CLASS)[0],
        stickyPriceEl = document.getElementsByClassName(STICKY_SELECTION_PRICE_CLASS)[0],
        stickyYourPriceContainerEl = document.getElementsByClassName(STICKY_YOUR_PRICE_CONTAINER_CLASS)[0],
        stickyYourPriceEl = document.getElementsByClassName(STICKY_SELECTION_YOUR_PRICE_CLASS)[0],
        catalogNumberEl = document.getElementsByClassName(CATALOG_NUMBER_CLASS)[0],
        catalogLabelEl = document.getElementsByClassName(CATALOG_LABEL_CLASS)[0],
        qtyContainerEl = document.getElementsByClassName(QTY_CONTAINER_CLASS)[0],
        cartControlsContainerEl = document.getElementsByClassName(CART_CONTROLS_CLASS)[0],
        restrictionMessageEl = document.getElementsByClassName(RESTRICTION_MESSAGE_CLASS)[0],
        addToCartButton = document.getElementsByClassName(ADD_TO_CART_BUTTON_CLASS)[0],
        productDiscontinuedDiv = document.getElementsByClassName(PRODUCT_DISCONTINUED_CLASS)[0],
        productEarlyAccessDiv = document.getElementsByClassName(PRODUCT_EARLYACCESS_CLASS)[0],
        productFastTrackDiv = document.getElementsByClassName(PRODUCT_FASTTRACK_CLASS)[0];

    // Hide all product page sections
    const allProductPageSections = document.getElementsByClassName(PRODUCT_PAGE_SECTION_CLASS),
        currentlyVisibleSpecificationsSection = document.querySelector(`.${SPECIFICATIONS_SECTION_CLASS}:not([hidden])`),
        productPageSectionCount = allProductPageSections.length;
    for (let i = 0; i < productPageSectionCount; i++) {
        allProductPageSections[i].hidden = true;
    }

    if (matchingCatalogNumber) {
        // Show the product page sections for this catalog number
        const matchingProductPageSections = document.querySelectorAll("." + PRODUCT_PAGE_SECTION_CLASS + '[data-catalog-number="' + matchingCatalogNumber.catNum + '"]'),
            matchCount = matchingProductPageSections.length;
        for (let i = 0; i < matchCount; i++) {
            matchingProductPageSections[i].hidden = false;
        }

        if (matchingCatalogNumber.listPrice || matchingCatalogNumber.yourPrice) {
            // Show the container
            if (pricingContainerEl) pricingContainerEl.classList.remove(PRICING_CONTAINER_HIDDEN_CLASS);
            if (stickyPriceContainerEl) stickyPriceContainerEl.classList.remove(STICKY_SELECTION_PRICE_CONTAINER_HIDDEN_CLASS);

            // List price
            if (matchingCatalogNumber.listPrice) {
                // Update list price
                listPriceEl.style.display = "inline";
                stickyPriceEl.style.display = "inline";
                if (listPriceEl) listPriceEl.innerText = matchingCatalogNumber.listPrice;
                if (stickyPriceEl) stickyPriceEl.innerText = matchingCatalogNumber.listPrice;
            }
            else {
                listPriceEl.style.display = "none";
                stickyPriceEl.style.display = "none";
            }

            if (matchingCatalogNumber.listPrice && matchingCatalogNumber.yourPrice && listPriceEl.tagName.toLowerCase() === "span" && (isUserLoggedIn() || inPunchoutSession())) {
                // If there is both a list price and customer-specific price, make sure the list price is displayed with a strikethrough
                const newListPriceEl = document.createElement("s");
                newListPriceEl.innerText = matchingCatalogNumber.listPrice;
                newListPriceEl.className = listPriceEl.className;
                listPriceEl.parentElement.replaceChild(newListPriceEl, listPriceEl);

                const newStickyYourPriceEl = document.createElement("s");
                newStickyYourPriceEl.innerText = matchingCatalogNumber.yourPrice;
                newStickyYourPriceEl.className = stickyPriceEl.className;
                stickyPriceEl.parentElement.replaceChild(newStickyYourPriceEl, stickyPriceEl);
            }
            else if (listPriceEl.tagName.toLowerCase() === "s" && !matchingCatalogNumber.yourPrice) {
                // Otherwise, make sure the list price is not displayed with a strikethrough
                const newListPriceEl = document.createElement("span");
                newListPriceEl.innerText = matchingCatalogNumber.listPrice;
                newListPriceEl.className = listPriceEl.className;
                listPriceEl.parentElement.replaceChild(newListPriceEl, listPriceEl);

                const newStickyYourPriceEl = document.createElement("span");
                newStickyYourPriceEl.innerText = matchingCatalogNumber.listPrice;
                newStickyYourPriceEl.className = stickyPriceEl.className;
                stickyPriceEl.parentElement.replaceChild(newStickyYourPriceEl, stickyPriceEl);
            }

            // Customer-specific price
            if (matchingCatalogNumber.yourPrice) {
                // A customer-specific price is available.
                if (yourPriceContainerEl) yourPriceContainerEl.style.display = "inline";
                if (stickyYourPriceContainerEl) stickyYourPriceContainerEl.style.display = "block";

                // Update customer-specific price
                if (yourPriceEl) yourPriceEl.innerText = matchingCatalogNumber.yourPrice;
                if (stickyYourPriceEl) stickyYourPriceEl.innerText = matchingCatalogNumber.yourPrice;
            }

            else {
                if (yourPriceContainerEl) yourPriceContainerEl.style.display = "none";
                if (stickyYourPriceContainerEl) stickyYourPriceContainerEl.style.display = "none";
            }
        }
        else {
            // Hide the container
            if (pricingContainerEl) pricingContainerEl.classList.add(PRICING_CONTAINER_HIDDEN_CLASS);
            if (stickyPriceContainerEl) stickyPriceContainerEl.classList.add(STICKY_SELECTION_PRICE_CONTAINER_HIDDEN_CLASS);
        }

        // Catalog number
        if (matchingCatalogNumber.catNum) {
            // Update the catalog number element, if it exists
            if (catalogNumberEl) catalogNumberEl.innerText = matchingCatalogNumber.catNum;

            // Show catalog number label container
            if (catalogLabelEl) catalogLabelEl.classList.remove(CATALOG_LABEL_HIDDEN_CLASS);
        }

        else {
            // Update the catalog number element
            if (catalogNumberEl) catalogNumberEl.innerText = "";

            // Hide catalog number label container
            if (catalogLabelEl) catalogLabelEl.classList.add(CATALOG_LABEL_HIDDEN_CLASS);
        }

        // Add to Cart and other actions (discontinued, please enquire, etc.)
        if (matchingCatalogNumber.restriction) {
            // Catalog number has a restriction
            if (matchingCatalogNumber.restriction.buttonText && matchingCatalogNumber.restriction.buttonHref) {
                // Hide the quantity controls
                if (qtyContainerEl) qtyContainerEl.classList.add(QTY_CONTAINER_HIDDEN_CLASS);
                if (productDiscontinuedDiv) productDiscontinuedDiv.classList.add(PRODUCT_DISCONTINUED_HIDDEN_CLASS);

                // Show the cart button and its container
                if (cartControlsContainerEl) cartControlsContainerEl.classList.remove(CART_CONTROLS_HIDDEN_CLASS);
                if (addToCartButton) addToCartButton.classList.remove(ADD_TO_CART_BUTTON_HIDDEN_CLASS);
            }
            else if (matchingCatalogNumber.restriction.status.toLowerCase() === "discontinued") {
                //product is discontinued but no link was set up, display default message
                if (cartControlsContainerEl) cartControlsContainerEl.classList.remove(CART_CONTROLS_HIDDEN_CLASS);
                if (qtyContainerEl) qtyContainerEl.classList.add(QTY_CONTAINER_HIDDEN_CLASS);
                if (addToCartButton) addToCartButton.classList.add(ADD_TO_CART_BUTTON_HIDDEN_CLASS);
                if (productDiscontinuedDiv) productDiscontinuedDiv.classList.remove(PRODUCT_DISCONTINUED_HIDDEN_CLASS);
            }
            else {
                // Hide button(s) for the specified alternate action
                if (cartControlsContainerEl) cartControlsContainerEl.classList.add(CART_CONTROLS_HIDDEN_CLASS);
                if (addToCartButton) addToCartButton.classList.add(ADD_TO_CART_BUTTON_HIDDEN_CLASS);
                if (productDiscontinuedDiv) productDiscontinuedDiv.classList.add(PRODUCT_DISCONTINUED_HIDDEN_CLASS);
            }

            if (matchingCatalogNumber.restriction.message && restrictionMessageEl) {
                // Display the restriction message
                restrictionMessageEl.classList.remove(RESTRICTION_MESSAGE_HIDDEN_CLASS);
                restrictionMessageEl.innerHTML = matchingCatalogNumber.restriction.message;
            }
            else {
                // Hide the restriction message
                restrictionMessageEl.classList.add(RESTRICTION_MESSAGE_HIDDEN_CLASS);
            }
        }
        else {
            // Show add to cart buttons
            if (qtyContainerEl) qtyContainerEl.classList.remove(QTY_CONTAINER_HIDDEN_CLASS);
            if (cartControlsContainerEl) cartControlsContainerEl.classList.remove(CART_CONTROLS_HIDDEN_CLASS);
            if (addToCartButton) addToCartButton.classList.remove(ADD_TO_CART_BUTTON_HIDDEN_CLASS);
            if (restrictionMessageEl) restrictionMessageEl.classList.add(RESTRICTION_MESSAGE_HIDDEN_CLASS);
            if (productDiscontinuedDiv) productDiscontinuedDiv.classList.add(PRODUCT_DISCONTINUED_HIDDEN_CLASS);
       }

        if (matchingCatalogNumber.sku == "EA") {
            if (productEarlyAccessDiv) productEarlyAccessDiv.classList.remove(PRODUCT_EARLYACCESS_HIDDEN_CLASS);
            if (productFastTrackDiv) productFastTrackDiv.classList.add(PRODUCT_FASTTRACK_HIDDEN_CLASS);
        }
        else if (matchingCatalogNumber.sku == "FT") {
            if (productEarlyAccessDiv) productEarlyAccessDiv.classList.add(PRODUCT_EARLYACCESS_HIDDEN_CLASS);
            if (productFastTrackDiv) productFastTrackDiv.classList.remove(PRODUCT_FASTTRACK_HIDDEN_CLASS);
        }
        else {
            if (productEarlyAccessDiv) productEarlyAccessDiv.classList.add(PRODUCT_EARLYACCESS_HIDDEN_CLASS);
            if (productFastTrackDiv) productFastTrackDiv.classList.add(PRODUCT_FASTTRACK_HIDDEN_CLASS);
        }
    }
    else {
        // Error case: a match was not found.
        if (catalogNumberEl) catalogNumberEl.innerText = "";
        if (catalogLabelEl) catalogLabelEl.classList.add(CATALOG_LABEL_HIDDEN_CLASS);
        if (cartControlsContainerEl) cartControlsContainerEl.classList.add(CART_CONTROLS_HIDDEN_CLASS);
        if (addToCartButton) addToCartButton.classList.add(ADD_TO_CART_BUTTON_HIDDEN_CLASS);
        if (pricingContainerEl) pricingContainerEl.classList.add(PRICING_CONTAINER_HIDDEN_CLASS);
    }

    // Build a string representing the currently-selected options
    const stickyConfigs = document.getElementsByClassName(STICKY_SELECTION_CONFIG_CLASS);
    if (stickyConfigs.length > 0) {
        const allSelectedOptions = document.querySelectorAll(".product-selection__option-control:checked"),
            selectedCount = allSelectedOptions.length;
        let selectionString = "";
        for (let i = 0; i < selectedCount; i++) {
            if (i > 0) selectionString += "/";

            let stringToAdd = "";
            if (allSelectedOptions[i].nodeName.toLowerCase() === "input") stringToAdd = allSelectedOptions[i].nextElementSibling.innerText;
            else stringToAdd = allSelectedOptions[i].innerText;

            selectionString += stringToAdd;
        }
        for (let i = 0; i < stickyConfigs.length; i++) {
            stickyConfigs[i].innerText = selectionString;
        }
    }

    // Update the query string
    if (history && history.replaceState) {
        history.replaceState(null, null, addOrReplaceQueryStringParameter(location, "catNum", matchingCatalogNumber.catNum));
    }

    // Tracking
    pushDetailImpression();
}

/**
 * @function updateValidSecondaryOptions
 * @description Refreshes the secondary options that are being displayed, showing/hiding and enabling/disabling them as necessary to ensure the current catalog selection state is valid.
 */
export function updateValidSecondaryOptions() {
    const allSelectedOptions = document.querySelectorAll(`.${OPTION_CONTROL_CLASS}:checked`),
        optionsToEnable = [];
    for (let i = 0; i < allSelectedOptions.length; i++) {
        const validOptionsString = allSelectedOptions[i].getAttribute("data-valid-secondary-options");
        if (validOptionsString) {
            const validOptionsArr = JSON.parse(validOptionsString);
            optionsToEnable.push.apply(optionsToEnable, validOptionsArr);
        }
    }

    // Get a list of all options that are not "primary" and enable/disable them according to the list of valid options for the current primary selection
    const allNonPrimaryOptionControls = document.querySelectorAll(`.${OPTION_CONTROL_CLASS}:not([name="product-selection--primary"])`),
        nonPrimaryOptionNames = [];
    for (let i = 0; i < allNonPrimaryOptionControls.length; i++) {
        const currentOption = allNonPrimaryOptionControls[i];
        if (optionsToEnable.indexOf(currentOption.value) !== -1) {
            // Item is a valid secondary option. Enable it.
            currentOption.removeAttribute("disabled");
        }
        else {
            // Item is not a valid secondary option. Disable it.
            currentOption.setAttribute("disabled", "disabled");
            if (currentOption.tagName === "OPTION" && currentOption.selected) {
                // If the option is selected, find the parent and clear its value (this assumes that we only use single-select dropdowns)
                const optionParent = currentOption.closest("select");
                if (optionParent) optionParent.value = null;
            }
            else currentOption.checked = false;
        }

        // Update the list of option names
        let currentNameToAdd = null;
        if (currentOption.tagName === "OPTION") {
            const selectParent = currentOption.closest(`.${OPTION_DROPDOWN_CLASS}`);
            if (selectParent) currentNameToAdd = selectParent.getAttribute("name");
        }
        else {
            currentNameToAdd = currentOption.getAttribute("name");
        }
        if (currentNameToAdd && nonPrimaryOptionNames.indexOf(currentNameToAdd) === -1) nonPrimaryOptionNames.push(currentNameToAdd);
    }

    // Iterate over the list of non-primary names: for each group, if a selection is required but none is made, select the default option in that group
    for (let i = 0; i < nonPrimaryOptionNames.length; i++) {
        autoSelectSecondaryOption(nonPrimaryOptionNames[i]);
    }

    // Check dropdowns. If all of a dropdown's child options are disabled, disable that dropdown.
    // Note: only checks items with the OPTION_CONTROL_CLASS. <option> elements such as "None" that omit that class will not be counted toward validation.
    const allDropdowns = document.getElementsByClassName(OPTION_DROPDOWN_CLASS);
    for (let i = 0; i < allDropdowns.length; i++) {
        if (allDropdowns[i].querySelectorAll(`.${OPTION_CONTROL_CLASS}:not(:disabled)`).length > 0) {
            // At least one option is not disabled
            allDropdowns[i].removeAttribute("disabled");
        }
        else {
            allDropdowns[i].setAttribute("disabled", "disabled");
        }
    }
}

/**
 * @function autoSelectSecondaryOption
 * @description Automatically selects a secondary option, provided one is enabled.
 * @param {String} secondaryOptionName The name attribute of the secondary option set to select
 */
export function autoSelectSecondaryOption(secondaryOptionName) {
    // Select the first secondary option that is not disabled
    if (secondaryOptionName) {
        // Try to find an option from this group
        let optionSelector = `.${OPTION_CONTROL_CLASS}[name="${secondaryOptionName}"]`,
            optionInGroup = document.querySelector(optionSelector);
        if (optionInGroup) {
            // We're looking at either a radio button or a checkbox.
            // Check if any items are currently selected
            if (document.querySelectorAll(`${optionSelector}:checked`).length === 0) {
                // Determine if a selection is required in this group.
                if (document.querySelectorAll(`${optionSelector}[required]`).length > 0) {
                    // Selection is required, but none is currently made.
                    // Select the first option in this name group that is required but not disabled:
                    const defaultSecondaryOption = document.querySelector(`${optionSelector}[required]:not(:disabled)`);
                    if (defaultSecondaryOption) {
                        defaultSecondaryOption.checked = true;
                        currentlySelectedCatalogNumber = null;
                    }
                }
            }
        }
        else {
            // Couldn't find an option by that name. Look for a dropdown box instead.
            optionInGroup = document.querySelector(`.${OPTION_DROPDOWN_CLASS}[name="${secondaryOptionName}"]`);
            if (optionInGroup) {
                if (optionInGroup.required && optionInGroup.value === "") {
                    // Select the first <option> in the dropdown. Assumes that a "None" option is prepended to every dropdown.
                    const defaultSecondaryOption = optionInGroup.querySelector(`option`);
                    if (defaultSecondaryOption) {
                        defaultSecondaryOption.selected = true;
                        currentlySelectedCatalogNumber = null;
                    }
                }
            }
        }
    }

    // Update the current catalog number if it was invalidated during the selection process
    if (currentlySelectedCatalogNumber === null) {
        const allSelectedOptions = getAllSelectedOptions();
        currentlySelectedCatalogNumber = getSelectedCatalogNumber(catalogNumbers, allSelectedOptions);
    }
}

/**
 * @function updateAddToCartButtonLink
 * @description Updates the "Add to Cart" button's URL to reflect the given catalog number.
 * @param {CatalogNumberDetails} matchingCatalogNumber The catalog number object
 */
export function updateAddToCartButtonLink(matchingCatalogNumber) {
    const addToCartButton = document.getElementsByClassName(ADD_TO_CART_BUTTON_CLASS)[0];

    if (addToCartButton) {
        if (matchingCatalogNumber) {
            if (matchingCatalogNumber.restriction) {

                if (matchingCatalogNumber.restriction.buttonText) {
                    addToCartButton.innerText = matchingCatalogNumber.restriction.buttonText;
                }
                else {
                    addToCartButton.innerText = '';
                }

                if (matchingCatalogNumber.restriction.buttonHref) {
                    addToCartButton.href = matchingCatalogNumber.restriction.buttonHref;
                }
                else {
                    addToCartButton.href = '';
                }

                if (matchingCatalogNumber.restriction.onclick) {
                    addToCartButton.setAttribute("onclick", matchingCatalogNumber.restriction.onclick);
                }
            }
            else {
                let cartString = commerceRedirectPath;
                cartString += `?qty=${currentQuantity}`;
                cartString += "&qpartno=" + matchingCatalogNumber.catNum;
                addToCartButton.setAttribute("href", cartString);
                addToCartButton.innerText = addToCartButton.getAttribute("data-default-text");
            }
        }
        else {
            addToCartButton.removeAttribute("href");
        }
    }
}

// From dataLayerPushes.js
function pushDetailImpression() {
    var cnList = document.querySelector("#gtmImpressions").value;
    if (cnList) {
        var catInfo = JSON.parse(cnList);
        var catObject = getSelectedCatalogNumber(catInfo, getAllSelectedOptions());

        //To send at each click
        try { eval(catObject.immpressionMsg); }
        catch (e) { console.log("Failed to push impressions. Ensure all parts and associated parts are published."); }
    }
}

/**
 * Function that sets the default File language on SDS Files to match the website country.
 * Non-mutating - returns new JSON data.
 * @param {string[]} cookiesArray - Array of language / country info ripped from site cookies.
 * @param {Object[]} countryJson - Array of objects representing possible SDS default language choices.
 * @param {JSON} originalSdsApiJson - JSON data from API call to widen to get SDS react container and Files.
 */
const setSdsLanguageToWeb = (cookiesArray, countryJson, originalSdsApiJson) => {
    const FALLBACK_LANGUAGE = 'English (United States)';
    // Start by clearning any default already in the response data.
    const clearedSdsApiJson = clearExistingDefault(originalSdsApiJson);
    const countryCode = cookiesArray[2];
    // Fallback to English USA if country code no present.
    if (countryCode === null || countryCode === undefined || countryCode === ' ') {
        return setDefaultSdsLanguage(clearedSdsApiJson, FALLBACK_LANGUAGE);
    }
    // Find a match between site country and countries in Files.
    const countryMatch = countryJson.find((e) => e.iso3 === countryCode);
    // If no match, fallback to US English.
    if (typeof countryMatch === undefined) {
        return setDefaultSdsLanguage(clearedSdsApiJson, FALLBACK_LANGUAGE);
    }
    // Else set default language to match country code.
    return setDefaultSdsLanguage(clearedSdsApiJson, countryMatch.name);
};

/**
 * Function that clears any set default language in JSON data.
 * Non-mutating - returns new JSON data.
 * @param {JSON} originalSdsApiJson - JSON data from API call to widen to get SDS react container and Files.
 */
const clearExistingDefault = (originalSdsApiJson) => {
    const modifiedSdsApiJson = JSON.parse(JSON.parse(JSON.stringify(originalSdsApiJson)));
    modifiedSdsApiJson.Files = modifiedSdsApiJson.Files.map((e) => ({ ...e, IsDefault: false }));
    return modifiedSdsApiJson;
};

/**
 * Function that sets default language in JSON data to the supplied language.
 * Non-mutating - returns new JSON data.
 * @param {JSON} originalSdsApiJson - JSON data from API call to widen to get SDS react container and Files.
 * @param {string} languageDefault - Language to set as default.
 */
const setDefaultSdsLanguage = (originalSdsApiJson, languageDefault) => {
    const matchingFiles = originalSdsApiJson.Files.filter((e) =>
        e.Language.includes(languageDefault)
    ).map((e) => ({ ...e, IsDefault: true }));
    const modifiedSdsApiJson = JSON.parse(JSON.stringify(originalSdsApiJson));
    modifiedSdsApiJson.Files = matchingFiles.concat(
        modifiedSdsApiJson.Files.filter((bo) => matchingFiles.every((ao) => ao.Language != bo.Language))
    );
    return modifiedSdsApiJson;
};

/**
 * Function that gets country and language information from site cookies.
 * Returns array of strings.
 */
const getSiteLanguageFromCookie = () => {
    const PREFERED_LANG_COOKIE = 'PreferredLanguage';
    const WEBSITE_LANG_COOKIE = 'website#lang';
    const COUNTRY_COOKIE = 'PromegaCountry';

    const cookiesArray = [PREFERED_LANG_COOKIE, WEBSITE_LANG_COOKIE, COUNTRY_COOKIE];

    function getCookie(name) {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) {
            return parts.pop().split(';').shift();
        }
        return null;
    }

    const resultsArray = [];
    for (let i = 0, j = cookiesArray.length; i < j; i++) {
        resultsArray.push(getCookie(cookiesArray[i]));
    }

    return resultsArray;
};

/**
 * Basic function that sets visibility of all loading spinners in SDS parent element.
 */
function hideLoadingSpinners() {
    const spinners = document.querySelectorAll('.lds-spinner');
    if (spinners.length > 0) {
        for (let i = 0, j = spinners.length; i < j; i++) {
            spinners[i].style.display = 'none';
        }
    }
}

/**
 * Make API call to widen to retrieve raw html for SDS FileDownload container.
 * Async by nature.
 * @param {string} partNumber - product part number.
 */
function getSdsControl(partNumber) {
    return $.ajax({
        url: `/services/widenlookupservice.svc/GetSdsControl?pn=${partNumber}`,
        dataType: 'html',
    });
}

/**
 * Appends html FileDownload container to existing parent node.
 * @param {Element} parentElement - existing DOM element to afix FileDownload container to.
 * @param {htmlString} reactContainer - response from calling getSdsControl function
 */
function appendSdsResponse(parentElement, reactContainer) {
    if (reactContainer) {
        $(parentElement).append(reactContainer);
    }
}

/**
 * Function creates array from all elements with class 'sdsControl' on page.
 * Function iterates through sdsControlElements array and uses data-catalog-number (product part number) in getSdsControl call.
 * componentsArray created containing objects that have pending promise from API call and corresponding part number.
 * Once API promises are resolved loop iteration replaces pending promise object properties in componentsArray with actual data.
 * Second loop iteration then calls appendSdsRespons after a match is found between part number - 
 * in object in componentsArray and catalogNumber in sdsControl element.
 * Finally call made to create and render React FileDownload component for each sdsControl element with a FileDownload container.
 */
function createSdsFiledownload() {
    const componentsArray = [];
    const sdsControlElements = Array.from(document.querySelectorAll('.sdsControl'));
    for (const sdsElement of sdsControlElements) {
        componentsArray.push({
            reactContainer: getSdsControl($(sdsElement).attr('data-catalog-number')),
            partNumber: $(sdsElement).attr('data-catalog-number'),
        });
    }

    let promises = [];
    for (const item of componentsArray) {
        promises.push(item.reactContainer);
    }

    const siteCountryCookies = getSiteLanguageFromCookie();

    Promise.all(promises)
        .then((results) => {
            let index = 0;
            for (let i = 0; i < results.length; i++) {
                componentsArray[index].reactContainer = results[i];
                ++index;
            }
            return componentsArray;
        })
        .then((componentsArray) => {
            hideLoadingSpinners();
            for (const entry of componentsArray) {
                for (const sdsAnchor of sdsControlElements) {
                    if (entry.reactContainer.length === 0 && entry.partNumber === sdsAnchor.dataset.catalogNumber) {
                        sdsAnchor.parentElement.style.display = "none";
                    }
                    if (entry.reactContainer.length !== 0 && entry.partNumber === sdsAnchor.dataset.catalogNumber) {
                        appendSdsResponse(sdsAnchor, entry.reactContainer);
                        // Create React component here
                        const props = setSdsLanguageToWeb(siteCountryCookies, supportedLanguages, sdsAnchor.querySelector('.download-file-container ').dataset.initialState);
                        ReactDOM.hydrate(React.createElement(FileDownload, props), sdsAnchor);
                        continue;
                    }
                }
            }
        })
        .catch((error) => {
            console.error(error.message);
        });
}

// Init code
{
    if (document.querySelector(`.${PRODUCT_PURCHASE_BLOCK_CLASS}`)) {
        createSdsFiledownload();
        try {
            catalogNumbers = cleanCatalogNumbers(JSON.parse(document.getElementsByClassName("product-purchase__valid-catalog-numbers")[0].getAttribute("value")));
            if (catalogNumbers === {}) catalogNumbers = [];
        }
        catch (ex) {
            catalogNumbers = [];
        }

        if (catalogNumbers.length > 0) {
            const allOptionControls = document.getElementsByClassName(OPTION_CONTROL_CLASS);
            for (let i = 0; i < allOptionControls.length; i++) {
                if (allOptionControls[i].tagName !== "OPTION") allOptionControls[i].addEventListener("change", optionControlChanged);
            }

            const allOptionDropdowns = document.getElementsByClassName(OPTION_DROPDOWN_CLASS);
            for (let i = 0; i < allOptionDropdowns.length; i++) {
                allOptionDropdowns[i].addEventListener("change", optionControlChanged);
            }

            document.addEventListener("click", stickyChangeConfigButtonClicked);

            // Get the commerce redirect path from the Add to Cart button
            let tempAddToCartButton = document.getElementsByClassName(ADD_TO_CART_BUTTON_CLASS)[0];
            if (tempAddToCartButton) {
                let hrefAttr = tempAddToCartButton.getAttribute("href"),
                    hrefParsed = hrefAttr.split("?")[0];
                if (hrefParsed) commerceRedirectPath = hrefParsed;
                tempAddToCartButton.addEventListener("click", trackAddToCartButtonClick);
            }

            renderQuantityInput();

            // Default to the first catalog number in the JSON
            let matchingCatNum = catalogNumbers[0].catNum;

            const referrerSearchQuery = getQueryStringParameter(document.referrer, "q").toLowerCase(),
                referrerCatNum = (referrerSearchQuery.match(/[A-Za-z]{1,2}\d{4}/) || "").toString(),
                queryStringCatNum = getQueryStringParameter(location.href, "catNum").toLowerCase() || getQueryStringParameter(location.href, "fq").toLowerCase();

            // If there's a catalog number from the referrer URL or the current query string, use that instead (giving preference to the query string)
            if (referrerCatNum || queryStringCatNum) {
                let foundRefferer = false;
                for (let i = 0; i < catalogNumbers.length; i++) {
                    const catNumLower = catalogNumbers[i].catNum.toLowerCase();
                    if (!foundRefferer && catNumLower === referrerCatNum) {
                        matchingCatNum = catalogNumbers[i].catNum;
                        foundRefferer = true;
                    }
                    else if (catNumLower === queryStringCatNum) {
                        matchingCatNum = catalogNumbers[i].catNum;
                        break;
                    }
                }
            }

            // Pre-select the matched catalog number
            selectCatalogNumber(matchingCatNum);
        }

        const stickyConfigTrigger = document.querySelector(`.${STICKY_SELECTION_CHANGE_CONFIG_CLASS}`);
        if (stickyConfigTrigger) stickyConfigTrigger.addEventListener("click", event => {
            trackEvent("Product Detail Page", "Click", "Opened sticky nav product flyout");
        });


        updateStickySelectionOnScroll();
        document.addEventListener("scroll", throttle(updateStickySelectionOnScroll, SCROLL_CHECK_INTERVAL));
    }

    //don't call this separately, updateDisplayedCatalogNumber is called when the page loads which makes its own call to pushDetailImpression
    // From dataLayerPushes.js
    //pushDetailImpression();
}